export interface RemoteIncident {
  number: string;
  state: string;
  short_description: string;
  sys_created_on: string;
  sys_id: string;
  priority: string;
  u_configuration_class: string;
  assigned_to: string;
  work_notes?: string;
  impacted_applications?: string | string[];
  ka_url?: string;
  // NEW FIELDS (ensure backend returns these names)
  incident_description?: string;
  business_impact?: string;
  bridge_details?: string;
  problem_record?: string;
  rca_owner?: string;
  email_subject?: string;
  circumvention_time?: string;
  email_recipients?: string
}

export interface FetchIncidentsResult {
  incidents: {
    id: string;
    title: string;
    // rename: status -> state (string from backend)
    state: string;
    severity: 'P4' | 'P3' | 'P2' | 'P1';
    updatedAt: string;
    configuration_class: string;
    assigned_to: string;
    priority?: string;
    description?: string;
    ka_url?: string;
    businessImpact?: string;
    bridgeDetails?: string;
    problemRecord?: string;
    owner?: string;
    email_subject?: string;
    circumvention_time?: string;
  }[];
  communications: {
    id: string;
    incidentId: string;
    author: string;
    role: string;
    timestamp: string;
    content: string;
  }[];
}

export interface IncidentUpdate {
  update_id: number;
  update: string;
  updated_by: string;
  timestamp: string | null;
  circumvented?: boolean;
  communication_sent?: boolean;
  approved?: boolean;
  approval_id?: number;
}

export interface PendingApproval {
  approval_id: number;
  status: string;
  approval_email: string;          
}

// New: minimal incident list shape (from merged endpoint DB records)
export interface MergedIncidentRecord {
  incident_number: string;
  sys_id: string;
  sys_created_on: string;
  short_description?: string; // <-- added
  u_configuration_class: string;
  priority: string;
  assigned_to: string;
  state: string;
  circumvented: boolean;
}

const mode = import.meta.env.MODE;
let baseurl: string;

if (mode === 'production') {
  baseurl = 'https://askawatch.cbre.com';
  console.log('FetchIncidents: Running in PRODUCTION mode');
} else if (mode === 'staging') {
  baseurl = 'https://askawatch-test.cbre.com';
  console.log('FetchIncidents: Running in non prod mode');
} else {
  baseurl = 'http://localhost:8000';
  console.log('FetchIncidents: Running in DEVELOPMENT mode');
}

// Optional override via env
const FETCH_URL =
  (import.meta as any).env.VITE_FETCH_INCIDENTS_URL ||
  `${baseurl}/api/fetch_incidents`;

// Add new configurable endpoints
const FETCH_INCIDENTS_MERGED_URL =
  (import.meta as any).env.VITE_FETCH_INCIDENTS_MERGED_URL ||
  `${baseurl}/api/incidents/merged`;

const FETCH_INCIDENT_BY_NUMBER_URL =
  (import.meta as any).env.VITE_FETCH_INCIDENT_BY_NUMBER_URL ||
  `${baseurl}/api/incidents/number`;

const SAVE_IMPACTED_APPS_URL =
  (import.meta as any).env.VITE_SAVE_IMPACTED_APPLICATIONS_URL ||
  `${baseurl}/api/incidents/impactedapps`;

const LLM_RESPONSE_UPDATES_URL = 
  (import.meta as any).env.VITE_LLM_RESPONSE_UPDATES_URL ||
  `${baseurl}/api/llm/generate_updates`;

const LLM_RESPONSE_EXECUTIVE_SUMMARY_URL = 
  (import.meta as any).env.VITE_LLM_RESPONSE_EXECUTIVE_SUMMARY_URL ||
  `${baseurl}/api/llm/generate_executive_summary`;

const KNOWLEDGE_ARTICLE_SAVE_URL =
  (import.meta as any).env.VITE_SAVE_KNOWLEDGE_ARTICLE_URL ||
  `${baseurl}/api/llm/generate_knowledge_article/save_docx`;

const APPROVE_UPDATE_URL =
  (import.meta as any).env.VITE_APPROVE_UPDATE_URL ||
  `${baseurl}/api/updates/approve_update`;

const EDIT_UPDATE_URL =
  (import.meta as any).env.VITE_EDIT_UPDATE_URL ||
  `${baseurl}/api/updates/edit_update`;

// Add incident updates endpoint
const INCIDENT_UPDATES_URL =
  (import.meta as any).env.VITE_INCIDENT_UPDATES_URL ||
  `${baseurl}/api/updates/get_updates`;

const REOPEN_UPDATES_URL =
  (import.meta as any).env.VITE_REOPEN_UPDATES_URL ||
  `${baseurl}/api/updates/reopen`;

const LLM_RESPONSE_KNOWLEDGE_ARTICLE =
  (import.meta as any).env.VITE_REOPEN_UPDATES_URL ||
  `${baseurl}/api/llm/generate_knowledge_article`;

const UPDATE_INCIDENT_META_URL =
  (import.meta as any).env.VITE_UPDATE_INCIDENT_META_URL ||
  `${baseurl}/api/incidents/update_meta`;

const EXECUTIVE_SUMMARY_URL =
  (import.meta as any).env.VITE_EXECUTIVE_SUMMARY_URL ||
  `${baseurl}/api/incidents/executive_summary`;

const priorityToSeverity = (
  p?: string
): FetchIncidentsResult['incidents'][number]['severity'] => {
  if (!p) return 'P4';
  const num = parseInt(p);
  if (num === 1) return 'P1';
  if (num === 2) return 'P2';
  if (num === 3) return 'P3';
  return 'P4';
};

function parseTimestamp(line: string): Date | null {
  // Matches: 2025-04-03 12:01:39 PM - Name (Work notes)
  const m = line.match(
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} (AM|PM))\s+-\s+.+$/i
  );
  if (!m) return null;
  const d = new Date(m[1]);
  return isNaN(d.getTime()) ? null : d;
}

// Robust work_notes parser
function parseWorkNotes(incidentNumber: string, workNotes?: string) {
  if (!workNotes) return [];
  const blocks = workNotes
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

  const communications: {
    id: string;
    incidentId: string;
    author: string;
    role: string;
    timestamp: string;
    content: string;
  }[] = [];

  let lastGoodDate = new Date(); // fallback
  let logicalIndex = 0;

  for (const rawBlock of blocks) {
    const lines = rawBlock.split('\n');
    const header = lines[0]?.trim() || '';
    let ts = parseTimestamp(header);
    if (ts) {
      lastGoodDate = ts;
      // Remove header line from content
      lines.shift();
    } else {
      // Not a timestamp header (email reply / forwarded text). Keep lastGoodDate.
    }

    const content = lines.join('\n').trim() || header;
    try {
      communications.push({
        id: `${incidentNumber}-wn-${logicalIndex++}`,
        incidentId: incidentNumber,
        author: 'system',
        role: 'system',
        timestamp: lastGoodDate.toISOString(),
        content
      });
    } catch {
      // Skip malformed block
    }
  }
  return communications;
}

// Helper to normalize impacted applications coming as:
// 1) JSON string of array of names: '["App1","App2"]'
// 2) JSON string of array of pairs: '[["Name A","APM0001234"],["Name B","APM0005678"]]'
// 3) Plain delimited string: 'App1, App2'
// 4) Array of names
function normalizeImpactedApps(raw: any): string[] | undefined {
  if (raw == null) return undefined;

  // If already an array
  if (Array.isArray(raw)) {
    // Could be ['App1','App2'] OR [['App1','APM123'],['App2','APM456']]
    if (raw.every(v => Array.isArray(v))) {
      // array of pairs
      return (raw as any[])
        .map(pair => Array.isArray(pair) ? pair[0] : null)
        .map(String)
        .map(s => s.trim())
        .filter(Boolean);
    }
    return (raw as any[])
      .map(String)
      .map(s => s.trim())
      .filter(Boolean);
  }

  // If string, attempt JSON parse first
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (/^\[.*\]$/.test(trimmed)) {
      try {
        const parsed = JSON.parse(trimmed);
        return normalizeImpactedApps(parsed) ?? [];
      } catch {
        // fall through to delimiter split
      }
    }
    // Delimited fallback
    return trimmed
      .split(/[,;]+/)
      .map(s => s.trim())
      .filter(Boolean);
  }

  return undefined;
}

// New: fetch merged incident list (no work notes)
export async function fetchMergedIncidents(): Promise<{
  incidents: FetchIncidentsResult['incidents'];
}> {
  const r = await fetch(FETCH_INCIDENTS_MERGED_URL, { method: 'GET' });
  if (!r.ok) throw new Error(`Merged incidents fetch failed: ${r.status}`);
  const json = await r.json();

  const records: MergedIncidentRecord[] = json.db_records || [];
  const incidents: FetchIncidentsResult['incidents'] = records.map((rec) => {
    const shortDesc = rec.short_description?.trim();
    return {
      id: rec.incident_number,
      title: shortDesc || rec.incident_number,
      // show actual backend state
      state: rec.state,
      severity: priorityToSeverity(rec.priority),
      updatedAt: rec.sys_created_on,
      priority: rec.priority,
      description: shortDesc || rec.incident_number,
      configuration_class: rec.u_configuration_class,
      assigned_to: rec.assigned_to,
      circumvented: rec.circumvented || false,
    } as any;
  });

  return { incidents };
}

// New: fetch single incident detail (by number) + parse work notes
export async function fetchIncidentDetails(incidentNumber: string): Promise<{
  incident?: FetchIncidentsResult['incidents'][number];
  communications: FetchIncidentsResult['communications'];
  impactedApps?: string[];
  emailRecipients?: EmailRecipient[];
}> {
  const url = `${FETCH_INCIDENT_BY_NUMBER_URL}?incident_number=${encodeURIComponent(
    incidentNumber
  )}`;
  const r = await fetch(url, { method: 'GET' });
  if (!r.ok) throw new Error(`Incident detail fetch failed: ${r.status}`);
  const json = await r.json();
  const list: RemoteIncident[] = json.incidents || [];
  const first = list[0];
  if (!first) {
    return { incident: undefined, communications: [], impactedApps: [], emailRecipients: [] };
  }

  const incident: FetchIncidentsResult['incidents'][number] = {
    id: first.number,
    title: first.short_description?.trim() || first.number,
    // show actual backend state
    state: first.state as any, // ensure RemoteIncident includes state if needed
    severity: priorityToSeverity(first.priority),
    updatedAt: first.sys_created_on,
    priority: first.priority,
    description: first.incident_description?.trim() || first.short_description?.trim(),
    configuration_class: first.u_configuration_class,
    assigned_to: first.assigned_to,
    ka_url: first.ka_url,
    businessImpact: first.business_impact?.trim(),
    bridgeDetails: first.bridge_details?.trim(),
    problemRecord: first.problem_record?.trim(),
    owner: first.rca_owner?.trim(),
    email_subject: first.email_subject?.trim(),
    circumvention_time: first.circumvention_time?.trim() // NEW
  };

  let communications: FetchIncidentsResult['communications'] = [];
  try {
    communications = parseWorkNotes(first.number, first.work_notes);
  } catch (e) {
    console.warn('Failed parsing work notes for', first.number, e);
  }

  const impactedApps = normalizeImpactedApps(first.impacted_applications);
  
  // ✅ Parse email recipients from DB (now expecting simple string list)
  let emailRecipients: EmailRecipient[] = [];
  if (first.email_recipients) {
    try {
      const parsed = typeof first.email_recipients === 'string' 
        ? JSON.parse(first.email_recipients) 
        : first.email_recipients;
      
      if (Array.isArray(parsed)) {
        // ✅ Handle both formats: ["email1", "email2"] OR [{email, appNumber}, ...]
        emailRecipients = parsed.map(item => {
          if (typeof item === 'string') {
            return { email: item };
          } else if (item && typeof item === 'object' && item.email) {
            return { email: item.email, appNumber: item.appNumber };
          }
          return null;
        }).filter((r): r is EmailRecipient => r !== null);
      }
    } catch (e) {
      console.warn('Failed parsing email_recipients', e);
    }
  }
  
  return { incident, communications, impactedApps, emailRecipients };
}

export async function saveImpactedApplications(
  incidentNumber: string,
  apps: string[],
  appPairs?: Array<[string, string | number | null]>,
  emailRecipients?: EmailRecipient[]  // ✅ NEW parameter
): Promise<{
  success: boolean;
  incident_number?: string;
  new_impacted_applications?: string;
  new_email_recipients?: EmailRecipient[];  // ✅ NEW
}> {
  console.log('saveImpactedApplications called with', { incidentNumber, apps, appPairs, emailRecipients });
  const r = await fetch(SAVE_IMPACTED_APPS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      incident_number: incidentNumber,
      impacted_application_pairs: appPairs || apps.map(a => [a, a]),
      email_recipients: emailRecipients  // ✅ NEW: Send email recipients
    })
  });
  if (!r.ok) throw new Error(`Save impacted apps failed: ${r.status}`);
  return r.json();
}

// ✅ Add EmailRecipient interface if not already defined
export interface EmailRecipient {
  email: string;
  appNumber?: string | number | null;
}

export async function generateLLMUpdates(
  userQuery: string,
): Promise<{
  success: boolean;
  suggested_update?: string;
}> {
  const r = await fetch(LLM_RESPONSE_UPDATES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_query: userQuery,
    })
  });
  if (!r.ok) throw new Error(`LLM generate updates failed: ${r.status}`);
  return r.json();
}


export async function generateLLMExecutiveSummary(
  userQuery: string,
): Promise<{
  success: boolean;
  suggested_update?: string;
}> {
  const r = await fetch(LLM_RESPONSE_EXECUTIVE_SUMMARY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_query: userQuery,
    })
  });
  if (!r.ok) throw new Error(`LLM generate updates failed: ${r.status}`);
  return r.json();
}

export async function generateLLMKnowledgeArticle(
  data: {
    incident_id: string;
    title: string;
    problem: string;
    impact: string;
    resolution_root_cause: string;
    next_steps: string;
    updates: string;
  }
): Promise<{
  success: boolean;
  incident_id: string;
  title: string;
  problem: string;
  impact: string;
  resolution_root_cause: string;
  next_steps: string;
  updates: string;
  combined_markdown?: string;
}> {
  const r = await fetch(LLM_RESPONSE_KNOWLEDGE_ARTICLE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!r.ok) throw new Error(`LLM KA generation failed: ${r.status}`);
  return r.json();
}

// Fetch all updates for an incident
export async function fetchIncidentUpdates(
  incidentNumber: string
): Promise<{
  success: boolean;
  incident_number: string;
  updates: IncidentUpdate[];
  count: number;
}> {
  const url = `${INCIDENT_UPDATES_URL}?incident_number=${encodeURIComponent(incidentNumber)}`;
  const r = await fetch(url, { method: 'GET' });
  if (!r.ok) throw new Error(`Fetch incident updates failed: ${r.status}`);
  const result = await r.json();
  console.log('fetchIncidentUpdates: response', result);
  return result;
}

// Add a new update
export async function addIncidentUpdate(
  incidentNumber: string,
  updateText: string,
  updatedBy?: string,
  circumvented?: boolean,
  userRole?: string         // NEW param (default false)
): Promise<{
  success: boolean;
  incident_number: string;
  incident_circumvented: boolean;
  inserted: IncidentUpdate;
  pending_approval: PendingApproval;
  count: number;
  updates: IncidentUpdate[];
}> {
  console.log('addIncidentUpdate: called with', updatedBy)
  const r = await fetch(INCIDENT_UPDATES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      incident_number: incidentNumber,
      update: updateText,
      updated_by: updatedBy,
      circumvented,                               // send flag to backend
      role: userRole                          // NEW: send user role
    })
  });
  if (!r.ok) throw new Error(`Add incident update failed: ${r.status}`);
  const result = await r.json();
  console.log('addIncidentUpdate: response', result);
  return result;
}

// Approve an update (marks approved = 1)
export async function approveIncidentUpdate(
  updateId: number
): Promise<{ success: boolean; update_id: number; approved: boolean }> {
  const url = `${APPROVE_UPDATE_URL}/${updateId}`;
  const r = await fetch(url, { method: 'GET' });
  if (!r.ok) throw new Error(`Approve update failed: ${r.status}`);
  return r.json();
}

// Edit + approve an update (sets new text, approved = 1)
export async function editIncidentUpdate(
  updateId: number,
  newUpdate: string
): Promise<{ success: boolean; update_id: number; new_update: string }> {
  const url = `${EDIT_UPDATE_URL}/${updateId}?${new URLSearchParams({
    new_update: newUpdate
  }).toString()}`;
  const r = await fetch(url, { method: 'GET' });
  if (!r.ok) throw new Error(`Edit update failed: ${r.status}`);
  return r.json();
}

// Delete an update (optionally include incident number for extra safety)
export async function deleteIncidentUpdate(
  updateId: number,
  incidentNumber?: string
): Promise<{
  success: boolean;
  deleted_count: number;
  deleted: IncidentUpdate & { incident_number: string; update: string };
  incident_number: string;
  remaining_count: number;
  remaining_updates: IncidentUpdate[];
}> {
  const url = `${INCIDENT_UPDATES_URL}/${updateId}${
    incidentNumber ? `?incident_number=${encodeURIComponent(incidentNumber)}` : ''
  }`;
  const r = await fetch(url, { method: 'DELETE' });
  if (!r.ok) throw new Error(`Delete incident update failed: ${r.status}`);
  return r.json();
}

export async function reopenIncidentUpdates(
  incidentNumber: string
): Promise<{ success: boolean; incident_number: string; updated_count: number }> {
  const url = `${REOPEN_UPDATES_URL}?incident_number=${encodeURIComponent(incidentNumber)}`;
  const r = await fetch(url, {
    method: 'POST'
  });
  if (!r.ok) throw new Error(`Reopen updates failed: ${r.status}`);
  return r.json();
}

export async function saveKnowledgeArticle(data: {
  incident_id: string;
  title: string;
  problem: string;
  impact: string;
  resolution_root_cause: string;
  next_steps: string;
  updates: string;
  combined_markdown?: string | null;
}): Promise<{
  success: boolean;
  incident_id: string;
  saved_id?: string;
  docx_url?: string;
  ka_url?: string;
}> {
  const r = await fetch(KNOWLEDGE_ARTICLE_SAVE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!r.ok) throw new Error(`Save knowledge article failed: ${r.status}`);
  return  r.json();
}

export async function updateIncidentMeta(data: {
  incident_number: string;
  description: string;
  businessImpact: string;
  bridgeDetails: string;
  problemRecord: string;
  rcaOwner: string;
  circumventionTime?: string; // NEW: optional circumvention time
}): Promise<{
  success: boolean;
  incident_number: string;
  updated: {
    description?: string;
    businessImpact?: string;
    bridgeDetails?: string;
    problemRecord?: string;
    rcaOwner?: string;
    circumventionTime?: string; // NEW
  };
}> {
  const r = await fetch(UPDATE_INCIDENT_META_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!r.ok) throw new Error(`Update incident meta failed: ${r.status}`);
  return r.json();
}

// Fetch stored executive summary for an incident
export async function fetchExecutiveSummary(
  incidentNumber: string
): Promise<{
  success: boolean;
  incident_number: string;
  executive_summary: string;
}> {
  const url = `${EXECUTIVE_SUMMARY_URL}?incident_number=${encodeURIComponent(incidentNumber)}`;
  const r = await fetch(url, { method: 'GET' });
  if (!r.ok) throw new Error(`Fetch executive summary failed: ${r.status}`);
  return r.json();
}

export async function saveEmailRecipientsOnly(
  incidentNumber: string,
  emailRecipients: EmailRecipient[]
): Promise<{
  success: boolean;
  incident_number?: string;
  new_email_recipients?: string[];
  saved_recipients_count?: number;
}> {
  const mode = import.meta.env.MODE;
  let baseurl: string;

  if (mode === 'production') {
    baseurl = 'https://askawatch.cbre.com';
  } else if (mode === 'staging') {
    baseurl = 'https://askawatch-test.cbre.com';
  } else {
    baseurl = 'http://localhost:8000';
  }

  const API_BASE = `${baseurl}/api`;
  
  const r = await fetch(`${API_BASE}/incidents/email_recipients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      incident_number: incidentNumber,
      email_recipients: emailRecipients
    })
  });
  
  if (!r.ok) throw new Error(`Save email recipients failed: ${r.status}`);
  return r.json();
}

export async function updateIncidentState(
  incidentNumber: string,
  state: string
): Promise<{ success: boolean; incident_number: string; old_state: string; new_state: string }> {
  const resp = await fetch(`${baseurl}/api/incidents/update_state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ incident_number: incidentNumber, state })
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}